import React, { useState, useEffect } from 'react';
import { maestrosAPI } from '@/lib/api';
import { Settings, Plus, Pencil, Trash2, ListTree, Check, X, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { BRAND, COLORS } from '@/lib/brand';

/**
 * AdminSettingsPage - Configuración del Sistema
 * 
 * NOTA: La personalización de identidad visual (logos, colores) ha sido eliminada.
 * La marca TUPU Stock es fija y administrada desde el archivo /lib/brand.js
 * 
 * Esta página solo permite gestionar configuraciones operativas:
 * - Maestros y categorías de productos
 * - (Futuras configuraciones operativas)
 */

const AdminSettingsPage = () => {
  const [maestros, setMaestros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingMaestro, setEditingMaestro] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    is_active: true
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadMaestros();
  }, []);

  const loadMaestros = async () => {
    try {
      const response = await maestrosAPI.getAll(false);
      setMaestros(response.data);
    } catch (error) {
      toast.error('Error al cargar maestros');
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setEditingMaestro(null);
    setFormData({
      name: '',
      description: '',
      is_active: true
    });
    setDialogOpen(true);
  };

  const openEditDialog = (maestro) => {
    setEditingMaestro(maestro);
    setFormData({
      name: maestro.name,
      description: maestro.description || '',
      is_active: maestro.is_active
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('El nombre es requerido');
      return;
    }

    setSaving(true);
    try {
      if (editingMaestro) {
        await maestrosAPI.update(editingMaestro.id, formData);
        toast.success('Maestro actualizado correctamente');
      } else {
        await maestrosAPI.create(formData);
        toast.success('Maestro creado correctamente');
      }
      setDialogOpen(false);
      loadMaestros();
    } catch (error) {
      toast.error(editingMaestro ? 'Error al actualizar maestro' : 'Error al crear maestro');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (maestro) => {
    if (!window.confirm(`¿Estás seguro de eliminar el maestro "${maestro.name}"?`)) {
      return;
    }

    try {
      await maestrosAPI.delete(maestro.id);
      toast.success('Maestro eliminado correctamente');
      loadMaestros();
    } catch (error) {
      toast.error('Error al eliminar maestro');
    }
  };

  const toggleActive = async (maestro) => {
    try {
      await maestrosAPI.update(maestro.id, { is_active: !maestro.is_active });
      toast.success(`Maestro ${maestro.is_active ? 'desactivado' : 'activado'}`);
      loadMaestros();
    } catch (error) {
      toast.error('Error al cambiar estado');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: COLORS.primary.DEFAULT }}></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-settings-page">
      <div>
        <h1 className="text-3xl font-bold" style={{ color: COLORS.text.primary }}>Configuración</h1>
        <p className="mt-1" style={{ color: COLORS.text.secondary }}>Ajustes del sistema</p>
      </div>

      {/* Sección de Maestros */}
      <div className="bg-white rounded-lg shadow-sm" style={{ border: `1px solid ${COLORS.border.DEFAULT}` }}>
        <div className="p-6 flex items-center justify-between" style={{ borderBottom: `1px solid ${COLORS.border.DEFAULT}` }}>
          <div className="flex items-center gap-3">
            <ListTree className="w-6 h-6" style={{ color: COLORS.primary.DEFAULT }} />
            <div>
              <h2 className="text-lg font-bold" style={{ color: COLORS.text.primary }}>Maestros y Sub-Maestros</h2>
              <p className="text-sm" style={{ color: COLORS.text.secondary }}>
                Gestiona las categorías de productos para pedidos
              </p>
            </div>
          </div>
          <Button 
            onClick={openCreateDialog} 
            data-testid="create-maestro-btn"
            style={{ backgroundColor: COLORS.primary.DEFAULT }}
          >
            <Plus className="w-4 h-4 mr-2" />
            Nuevo Maestro
          </Button>
        </div>

        <div className="p-6">
          {maestros.length === 0 ? (
            <div className="text-center py-12">
              <ListTree className="w-16 h-16 mx-auto mb-4" style={{ color: COLORS.border.DEFAULT }} />
              <h3 className="text-lg font-semibold mb-2" style={{ color: COLORS.text.primary }}>No hay maestros creados</h3>
              <p className="mb-4" style={{ color: COLORS.text.secondary }}>
                Crea maestros para categorizar tus productos en los pedidos
              </p>
              <Button onClick={openCreateDialog} style={{ backgroundColor: COLORS.primary.DEFAULT }}>
                <Plus className="w-4 h-4 mr-2" />
                Crear primer maestro
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {maestros.map((maestro, index) => (
                <motion.div
                  key={maestro.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className={`flex items-center justify-between p-4 rounded-lg ${
                    maestro.is_active ? 'bg-white' : 'bg-slate-50 opacity-60'
                  }`}
                  style={{ border: `1px solid ${COLORS.border.DEFAULT}` }}
                  data-testid={`maestro-item-${maestro.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div 
                      className="w-10 h-10 rounded-lg flex items-center justify-center"
                      style={{ 
                        backgroundColor: maestro.is_active ? COLORS.primary.light : COLORS.background.secondary 
                      }}
                    >
                      <ListTree 
                        className="w-5 h-5" 
                        style={{ color: maestro.is_active ? COLORS.primary.DEFAULT : COLORS.text.muted }}
                      />
                    </div>
                    <div>
                      <h3 className="font-semibold" style={{ color: COLORS.text.primary }}>{maestro.name}</h3>
                      {maestro.description && (
                        <p className="text-sm" style={{ color: COLORS.text.secondary }}>{maestro.description}</p>
                      )}
                      <span 
                        className={`inline-flex items-center gap-1 text-xs mt-1 px-2 py-0.5 rounded-full ${
                          maestro.is_active 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-slate-200 text-slate-600'
                        }`}
                      >
                        {maestro.is_active ? (
                          <>
                            <Check className="w-3 h-3" />
                            Activo
                          </>
                        ) : (
                          <>
                            <X className="w-3 h-3" />
                            Inactivo
                          </>
                        )}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleActive(maestro)}
                      title={maestro.is_active ? 'Desactivar' : 'Activar'}
                    >
                      {maestro.is_active ? (
                        <X className="w-4 h-4" style={{ color: COLORS.text.muted }} />
                      ) : (
                        <Check className="w-4 h-4" style={{ color: COLORS.semantic.success }} />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEditDialog(maestro)}
                      data-testid={`edit-maestro-${maestro.id}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(maestro)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      data-testid={`delete-maestro-${maestro.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>

        {/* Nota informativa sobre maestros */}
        <div className="px-6 pb-6">
          <div className="rounded-lg p-4" style={{ backgroundColor: COLORS.primary.light, border: `1px solid ${COLORS.primary[200]}` }}>
            <h4 className="font-semibold mb-2" style={{ color: COLORS.primary[700] }}>¿Cómo usar los maestros?</h4>
            <ul className="text-sm space-y-1" style={{ color: COLORS.primary[600] }}>
              <li>• Los maestros categorizan productos para facilitar los pedidos</li>
              <li>• Asigna maestros a productos desde la sección "Productos"</li>
              <li>• Los usuarios de local pueden filtrar productos por maestro al hacer pedidos</li>
              <li>• Un producto puede pertenecer a múltiples maestros</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Información del Sistema */}
      <div className="bg-white rounded-lg shadow-sm" style={{ border: `1px solid ${COLORS.border.DEFAULT}` }}>
        <div className="p-6 flex items-center gap-3" style={{ borderBottom: `1px solid ${COLORS.border.DEFAULT}` }}>
          <Info className="w-6 h-6" style={{ color: COLORS.primary.DEFAULT }} />
          <div>
            <h2 className="text-lg font-bold" style={{ color: COLORS.text.primary }}>Información del Sistema</h2>
            <p className="text-sm" style={{ color: COLORS.text.secondary }}>
              Detalles de la aplicación
            </p>
          </div>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="p-4 rounded-lg" style={{ backgroundColor: COLORS.background.secondary }}>
              <p className="text-sm font-medium mb-1" style={{ color: COLORS.text.secondary }}>Nombre</p>
              <p className="text-lg font-bold" style={{ color: COLORS.text.primary }}>{BRAND.name}</p>
            </div>
            <div className="p-4 rounded-lg" style={{ backgroundColor: COLORS.background.secondary }}>
              <p className="text-sm font-medium mb-1" style={{ color: COLORS.text.secondary }}>Versión</p>
              <p className="text-lg font-bold" style={{ color: COLORS.text.primary }}>{BRAND.version}</p>
            </div>
            <div className="p-4 rounded-lg" style={{ backgroundColor: COLORS.background.secondary }}>
              <p className="text-sm font-medium mb-1" style={{ color: COLORS.text.secondary }}>Descripción</p>
              <p className="text-lg font-bold" style={{ color: COLORS.text.primary }}>{BRAND.tagline}</p>
            </div>
          </div>

          <div className="mt-6 p-4 rounded-lg" style={{ backgroundColor: COLORS.accent.light, border: `1px solid ${COLORS.accent[200]}` }}>
            <p className="text-sm" style={{ color: COLORS.accent[700] }}>
              <strong>Nota:</strong> La identidad visual de {BRAND.name} es administrada de forma centralizada 
              y no puede ser modificada desde esta interfaz.
            </p>
          </div>
        </div>
      </div>

      {/* Dialog para crear/editar maestro */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingMaestro ? 'Editar Maestro' : 'Nuevo Maestro'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="name">Nombre *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ej: Cocina"
                  className="mt-2"
                  data-testid="maestro-name-input"
                />
              </div>
              <div>
                <Label htmlFor="description">Descripción</Label>
                <Input
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Descripción opcional del maestro"
                  className="mt-2"
                  data-testid="maestro-description-input"
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="rounded"
                  data-testid="maestro-active-checkbox"
                />
                <Label htmlFor="is_active" className="cursor-pointer">
                  Activo
                </Label>
              </div>
            </div>
            <DialogFooter>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setDialogOpen(false)}
              >
                Cancelar
              </Button>
              <Button 
                type="submit" 
                disabled={saving}
                data-testid="maestro-submit-btn"
                style={{ backgroundColor: COLORS.primary.DEFAULT }}
              >
                {saving ? 'Guardando...' : (editingMaestro ? 'Guardar cambios' : 'Crear maestro')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminSettingsPage;
